import { useState } from "react"

import { Database, RefreshCw, Sparkles } from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { QueryBinding } from "./catalog"

import { Button } from "@/components/ui/button"

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"

import {
  useBindingForPath,
  useBindingsCanViewSql,
  useBindingsDashboardId,
} from "./bindings-context"

/**
 * Element types that don't surface data the agent could "refresh".
 * Showing the AI-refresh / Live-SQL badge on these adds visual noise
 * with no signal — a Heading or Checkbox isn't waiting on a SQL run.
 * Container types (Card, Row, Grid, Tabs, Accordion) are also excluded
 * because their refresh state is whatever their children's is.
 */
const NON_DATA_ELEMENT_TYPES = new Set<string>([
  "Text",
  "Heading",
  "Badge",
  "Avatar",
  "Tooltip",
  "Separator",
  "Checkbox",
  "Select",
  "TextInput",
  "ProgressBar",
  "Card",
  "Row",
  "Grid",
  "Tabs",
  "Accordion",
  "Repeat",
])

/**
 * Floating icon in the top-right corner of data tiles. Icon-only at
 * rest — the textual label drowned out tile titles when the dashboard
 * was scanned end-to-end. Hover surfaces the full label + reason via
 * a shadcn Tooltip; click on bound tiles still opens the SQL popover.
 *
 * - Database icon (green) when the tile has a bound query — refresh runs
 *   the SQL directly in ~2s.
 * - Sparkles icon (amber) when no binding exists — refresh falls through
 *   to the agent loop (minutes). Lets admins see at a glance which tiles
 *   are enterprise-ready before sharing the dashboard.
 *
 * Returns null for atomic widgets (Heading, Checkbox, …) and containers
 * — those don't have data to refresh, so a refresh-state badge would be
 * misleading. Pass ``elementType`` so the wrapper can skip the badge
 * before mounting the popover/tooltip subtree.
 */
export function TileBindingBadge({
  elementPath,
  elementType,
}: {
  elementPath: string | undefined
  elementType?: string
}) {
  const { t } = useTranslation()
  const binding = useBindingForPath(elementPath)
  const canViewSql = useBindingsCanViewSql()

  if (!elementPath) return null
  if (elementType && NON_DATA_ELEMENT_TYPES.has(elementType)) return null

  if (!binding) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              aria-label={t("dash.binding.aiRefresh", "AI refresh")}
              className={cn(
                "pointer-events-auto absolute top-2 right-2 z-10",
                "inline-flex size-6 items-center justify-center rounded-full border",
                "border-warning-ink/40 bg-warning/30 text-warning-ink",
                "cursor-help"
              )}
            >
              <Sparkles className="size-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs" side="left">
            <p className="font-medium">{t("dash.binding.aiRefresh", "AI refresh")}</p>
            <p className="text-muted-foreground">
              {t(
                "dash.binding.noBindingTooltip",
                "No SQL binding — refresh re-runs the agent (slow)"
              )}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  // Readers see the "Live SQL" badge but no popover — the actual SQL,
  // connection metadata, and state-param wiring stay hidden so a shared
  // dashboard doesn't double as a schema-disclosure channel.
  if (!canViewSql) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              aria-label={t("dash.binding.liveSql", "Live SQL")}
              className={cn(
                "pointer-events-auto absolute top-2 right-2 z-10",
                "inline-flex size-6 items-center justify-center rounded-full border",
                "border-success bg-success/15 text-success",
                "cursor-help"
              )}
            >
              <Database className="size-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs" side="left">
            <p className="font-medium">{t("dash.binding.liveSql", "Live SQL")}</p>
            <p className="text-muted-foreground">
              {t(
                "dash.binding.boundReaderTooltip",
                "Bound to a SQL query — only editors can inspect the query."
              )}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <Popover>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                aria-label={t("dash.binding.liveSql", "Live SQL")}
                data-testid="tile-binding-badge-live"
                size="sm"
                variant="outline"
                className={cn(
                  "absolute top-2 right-2 z-10 h-6 w-6 rounded-full p-0",
                  "border-success bg-success/15 text-success hover:bg-success/25"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <Database className="size-3.5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs" side="left">
            <p className="font-medium">{t("dash.binding.liveSql", "Live SQL")}</p>
            <p className="text-muted-foreground">
              {t(
                "dash.binding.boundTooltip",
                "Bound to a SQL query — refreshes in seconds. Click for details."
              )}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        align="end"
        className="w-[480px] max-w-[90vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <TileBindingDetails binding={binding} />
      </PopoverContent>
    </Popover>
  )
}

function TileBindingDetails({ binding }: { binding: QueryBinding }) {
  const { t } = useTranslation()
  const paramEntries = Object.entries(binding.params_from_state ?? {})
  const dashboardId = useBindingsDashboardId()
  const [refreshing, setRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const canRefresh = !!(dashboardId && binding.binding_id)

  async function handleRefresh() {
    if (!canRefresh || refreshing) return
    setRefreshing(true)
    setRefreshError(null)
    try {
      const res = await api.post<
        unknown,
        {
          spec?: Record<string, unknown>
          query_bindings?: unknown[]
        }
      >(`/dashboards/${dashboardId}/bindings/${binding.binding_id}/refresh`, {})
      // Push the fresh spec into the store instead of full page reload.
      // The reload approach drops the user back to the dashboards
      // landing page because the open dashboard isn't deep-linked in
      // the URL — currentDashboardId is in-memory only.
      const updateEvent = new CustomEvent("dash:dashboard-refreshed", { detail: res })
      window.dispatchEvent(updateEvent)
    } catch (err) {
      logger.warn("Per-tile refresh failed", err as Error)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      setRefreshError(String((err as Error)?.message ?? err))
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{t("dash.binding.popoverTitle", "Bound SQL query")}</h3>
        <div className="flex items-center gap-2">
          {canRefresh && (
            <Button
              disabled={refreshing}
              size="sm"
              variant="outline"
              className="h-7 gap-1 text-xs"
              onClick={handleRefresh}
            >
              <RefreshCw className={cn("size-3", refreshing && "animate-spin")} />
              {refreshing
                ? t("dash.binding.refreshing", "Uppdaterar...")
                : t("dash.binding.refreshNow", "Uppdatera")}
            </Button>
          )}
          <span className="text-muted-foreground text-xs">
            {t("dash.binding.target", "Target")}: <code>{binding.target_field}</code>
          </span>
        </div>
      </div>
      {refreshError && <p className="text-destructive text-xs">{refreshError}</p>}

      <dl className="text-muted-foreground grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <dt>{t("dash.binding.connection", "Connection")}</dt>
        <dd className="text-foreground font-mono">{binding.connection_id}</dd>
        <dt>{t("dash.binding.element", "Element")}</dt>
        <dd className="text-foreground font-mono">{binding.element_path}</dd>
        {binding.last_success && (
          <>
            <dt>{t("dash.binding.lastSuccess", "Last success")}</dt>
            <dd className="text-foreground">{formatTimestamp(binding.last_success)}</dd>
          </>
        )}
        {binding.last_error && (
          <>
            <dt>{t("dash.binding.lastError", "Last error")}</dt>
            <dd className="text-destructive">{binding.last_error}</dd>
          </>
        )}
      </dl>

      <div>
        <div className="text-muted-foreground mb-1 text-xs tracking-wide uppercase">
          {t("dash.binding.sql", "SQL")}
        </div>
        <pre className="bg-muted max-h-[320px] overflow-auto rounded p-2 font-mono text-xs">
          {binding.sql}
        </pre>
      </div>

      {paramEntries.length > 0 && (
        <div>
          <div className="text-muted-foreground mb-1 text-xs tracking-wide uppercase">
            {t("dash.binding.params", "State parameters")}
          </div>
          <ul className="space-y-0.5 text-xs">
            {paramEntries.map(([placeholder, pointer]) => (
              <li key={placeholder} className="font-mono">
                <span className="text-foreground">{placeholder}</span>
                <span className="text-muted-foreground"> ← </span>
                <span className="text-foreground">{pointer}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-muted-foreground border-t pt-2 text-xs">
        {t(
          "dash.binding.editHint",
          "Editing is coming next — for now, ask the Dash agent to rewrite this query."
        )}
      </div>
    </div>
  )
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString()
  } catch {
    return iso
  }
}
